import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import ReactTooltip from 'react-tooltip';
import { formatAmount, formatPercent } from '../../helpers/quickbooks';

const formatCount = (value) => value === null || value === undefined ? '-' : Number(value).toLocaleString('en-US');

const AmazonProfitLossTable = ({ section, months, tooltipFor }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (key) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const formatValue = (row, value) => {
    if (value === null || value === undefined) return '-';
    if (row.format === 'count') return formatCount(value);
    return formatAmount(value);
  };

  const formatPercentage = (row) => {
    if (row.percentOfSales === null || row.percentOfSales === undefined) return '-';
    return formatPercent(Number(row.percentOfSales) * 100);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-6 py-3">
        <h3 className="text-base font-bold text-stone-900">{section.label}</h3>
      </div>
      <div className="relative overflow-x-auto">
        <table className="min-w-max w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-20 min-w-[240px] bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">Account</th>
              <th className="min-w-[80px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">GL</th>
              {months.map((month) => (
                <th key={month} className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">{month}</th>
              ))}
              <th className="sticky right-[110px] z-10 border-l border-gray-200 bg-gray-50 px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">YTD</th>
              <th className="sticky right-0 z-10 w-[110px] border-l border-gray-200 bg-gray-50 px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">% of Sales</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {section.rows.map((row) => {
              if (row.isDetail && !expanded.has(row.parentKey)) return null;
              const tooltip = tooltipFor(row);
              const tooltipId = `amazon-pl-${section.key}-${row.key}`;
              const isUnmapped = row.key === 'unmappedTotal' && Number(row.ytd) !== 0;
              const rowClass = isUnmapped
                ? 'bg-amber-50 text-amber-900 hover:bg-amber-100'
                : 'hover:bg-gray-50';
              const stickyBackground = isUnmapped ? 'bg-amber-50' : 'bg-white';
              return (
                <tr key={row.key} className={rowClass}>
                  <td className={`sticky left-0 z-10 min-w-[240px] whitespace-nowrap ${stickyBackground} px-6 py-4 text-sm ${row.isSubtotal ? 'font-bold' : ''} ${row.isDetail ? 'pl-12 text-zinc-600' : ''}`}>
                    <span className="inline-flex items-center gap-1">
                      {row.hasDetail && (
                        <button
                          type="button"
                          className="rounded text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          aria-label={`Toggle ${row.label} breakout`}
                          aria-expanded={expanded.has(row.key)}
                          onClick={() => toggleExpanded(row.key)}
                        >
                          {expanded.has(row.key)
                            ? <ChevronDownIcon className="h-4 w-4" />
                            : <ChevronRightIcon className="h-4 w-4" />}
                        </button>
                      )}
                      {row.label}
                      {tooltip && (
                        <>
                          <button
                            type="button"
                            className="rounded text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            aria-label={`More information about ${row.label}`}
                            data-tip
                            data-for={tooltipId}
                          >
                            <InformationCircleIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </span>
                  </td>
                  <td className={`whitespace-nowrap px-6 py-4 text-sm ${row.isSubtotal ? 'font-bold' : ''}`}>{row.gl || '—'}</td>
                  {months.map((month) => {
                    const value = row.months?.[month];
                    return (
                      <td key={month} className={`whitespace-nowrap px-6 py-4 text-right text-sm tabular-nums ${Number(value) < 0 ? 'text-red-500' : ''} ${row.isSubtotal ? 'font-bold' : ''}`}>
                        {formatValue(row, value)}
                      </td>
                    );
                  })}
                  <td className={`sticky right-[110px] z-[1] whitespace-nowrap border-l border-gray-200 ${stickyBackground} px-6 py-4 text-right text-sm font-bold tabular-nums ${Number(row.ytd) < 0 ? 'text-red-500' : 'text-stone-900'}`}>
                    {formatValue(row, row.ytd)}
                  </td>
                  <td className="sticky right-0 z-[1] w-[110px] whitespace-nowrap border-l border-gray-200 bg-gray-50 px-6 py-4 text-right text-sm font-bold tabular-nums text-blue-600">
                    {formatPercentage(row)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {section.rows.map((row) => {
        const tooltip = tooltipFor(row);
        if (!tooltip) return null;
        return (
          <ReactTooltip
            key={`tooltip-${section.key}-${row.key}`}
            id={`amazon-pl-${section.key}-${row.key}`}
            effect="solid"
            place="bottom"
            className="max-w-[400px] whitespace-normal text-left text-xs"
          >
            {tooltip}
          </ReactTooltip>
        );
      })}
    </div>
  );
};

export default AmazonProfitLossTable;
